<template>
  <div v-if="destination">
    <div class="py-16 relative">
      <div
        class="absolute inset-0 bg-cover bg-center"
        :style="{
          backgroundImage: destination?.image_header?.url
            ? `url(${destination?.image_header?.url})`
            : destination?.images[0]?.url
              ? `url(${destination?.images[0]?.url})`
              : undefined,
        }"
      >
        <div
          class="absolute inset-0 bg-gradient-to-r from-blue-950 to-cyan-700 opacity-[0.85]"
        ></div>
      </div>
      <ASiteContainer class="relative z-10">
        <h1 class="sm:text-5xl text-4xl font-black mb-3 drop-shadow text-white">
          {{ destination?.titleHeader }}
        </h1>
        <div class="flex items-center gap-2 mb-3">
          <span class="text-amber-400 font-bold pb-0.5">{{ destination?.rating }}/5</span>
          <NuxtRating
            border-color="#fbbf24"
            active-color="#f59e0b"
            inactive-color="#fff"
            :rating-step="0.1"
            :rounded-corners="true"
            :border-width="5"
            :rating-size="10"
            :rating-value="destination?.rating" />
          <span class="text-amber-400 pb-0.5">{{ $t('pages.destination.reviews', { count: destination?.rating_count }) }}</span>
        </div>
        <p class="sm:text-2xl text-lg font-light italic text-white mb-4">
          {{ destination?.subtitleHeader1 }}
        </p>
        <p class="sm:text-xl text-lg text-white sm:pr-24">
          {{ destination?.subtitleHeader2 }}
        </p>
        <div class="flex sm:flex-row flex-col items-start justify-start gap-x-4 gap-y-4 mt-12">
          <UButton
            size="xl"
            trailing-icon="mdi:arrow-right-bold"
            class="font-semibold rounded-xl px-6 sm:self-center bg-gradient-to-r shadow-sm"
            @click="newQuoteWithDestination(destination.id)">
            {{ $t('pages.destination.createTrip') }}
          </UButton>

          <UButton
            size="xl"
            variant="outline"
            color="white"
            class="font-semibold rounded-xl px-6 sm:self-center text-white border-white hover:bg-white hover:text-blue-950 duration-300"
            @click="scrollToTrips()">
            {{ $t('pages.destination.allTrips') }}
          </UButton>
        </div>
      </ASiteContainer>
    </div>

    <ASiteContainer v-show="trips && trips.length > 0" class="mb-16">
      <APageSection class="trips-section">
        <h2 class="text-4xl font-black my-6">
          {{ $t('pages.destination.slug.trips') }}
        </h2>

        <TripsPaginate :limit="12" :showSearchForm="false" :destinationId="destination.id" @fetched="trips = $event" />
      </APageSection>
    </ASiteContainer>

    <div class="w-full pb-16 pt-14  bg-slate-50/80">
      <ASiteContainer>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
          <div id="left-column-banner" class="flex flex-col items-start sm:pr-10">
            <h3 class="text-3xl font-black mb-6">{{ $t('pages.destination.whenToGo', { destination: destination?.name }) }}</h3>
            <ClientOnly>
              <div class="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-x-2 sm:gap-x-4 w-full">
                <div v-for="(rating, monthIndex) in destination?.weather_rating" :key="monthIndex" class="flex flex-col items-center">
                  <div class="font-medium text-sm sm:text-base">{{ monthName(monthIndex) }}</div>
                  <div v-if="rating === 1" class="text-xl sm:text-2xl text-green-500 mt-2">
                    <UIcon class="text-3xl" name="mdi:emoticon-excited-outline" />
                  </div>
                  <div v-else-if="rating === 2" class="text-xl sm:text-2xl text-orange-500 mt-2">
                    <UIcon class="text-3xl" name="mdi:emoticon-happy-outline" />
                  </div>
                </div>
              </div>
            </ClientOnly>
            <p class="mt-6 text-sm sm:text-base text-justify whitespace-pre-line">
              {{ destination?.weather }}
            </p>

            <h4 class="text-xl font-black mt-10 mb-6">{{ $t('pages.destination.activities') }}</h4>
            <ul class="list-disc pl-5 space-y-2 text-sm sm:text-base">
              <li v-for="activity in destination?.destination_activities" :key="activity.name">
                <strong>{{ activity.name }}</strong> - {{ activity.description }}
              </li>
            </ul>
          </div>
          <div class="w-full">
            <h3 class="text-3xl font-black mb-4">{{ $t('pages.destination.mustSeeLocations') }}</h3>
            <ACarousel
              v-slot="{ item }"
              :space-between="10"
              :pagination="{
                enabled: !$device.isMobile,
                clickable: true,
                renderBullet: (index: number, className: string) => {
                  return `<span class='${className}'>${index + 1}</span>`
                },
              }"
              :free-mode="true"
              :style="$device.isMobile ? '' : `max-height: ${bannerHeight}px`"
              class="h-full"
              :breakpoints="{
                320: {
                  slidesPerView: 1.1,
                },
                768: {
                  slidesPerView: 'auto',
                },
              }"
              :show-navigation="false"
              :items="destination?.destination_pois"
              :direction="$device.isMobile ? 'horizontal' : 'vertical'">
              <div class="bg-white border border-gray-200 rounded-lg p-4">
                <div class="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <AImg
                    v-if="item.image?.url"
                    width="700"
                    height="500"
                    :src="item.image.url"
                    quality="90"
                    :alt="item.name"
                    class="w-full sm:max-h-48 sm:max-w-64 sm:min-w-64 max-h-48 sm:grow shadow-lg shadow-gray-100 object-cover rounded-lg" />
                  <div class="flex flex-col items-start pr-4 mt-3 sm:mt-0">
                    <p class="text-[1.15rem] font-extrabold leading-5 pb-2">{{ item.name }}</p>
                    <p class="text-gray-800">
                      {{ item.description }}
                    </p>
                  </div>
                </div>
              </div>
            </ACarousel>
          </div>
        </div>
      </ASiteContainer>
    </div>

    <ASiteContainer>
      <APageSection>
        <h2 class="text-4xl font-black my-6">{{ destination?.title }}</h2>

        <ImagesCarousel
          v-if="destination?.images"
          :items="
            destination?.images.map((image) => ({
              image: { name: image.title, url: image.url },
            }))
          "
          class="mb-6 self-stretch w-full"
        />

        <div
          class="mb-6 whitespace-pre-line"
          v-html="destination?.description"
        ></div>

        <div class="flex flex-col my-12 space-y-24">
          <div
            v-for="(description, index) in destination?.destinationDescriptions"
            :key="index"
            class="flex items-center"
            :class="[
              index % 2 === 0 && description.image.src
                ? 'flex-col sm:flex-row'
                : 'flex-col sm:flex-row-reverse',
            ]"
          >
            <AImg
              v-if="description.image.src"
              :src="description.image.src"
              :alt="description.image.alt"
              width="640"
              height="400"
              class="w-full sm:w-1/2 rounded-2xl shadow-lg object-cover sm:max-h-[500px]"
            />
            <div
              class="flex-1 w-full"
              :class="[
                index % 2 === 0 && description.image.src
                  ? 'sm:pl-12'
                  : 'sm:pr-12 mr-0 sm:mr-4 mb-4 sm:mb-0',
              ]"
            >
              <h3 class="text-[2rem] leading-9 font-black mb-6 sm:mt-0 mt-8">
                {{ description.title }}
              </h3>
              <div
                class="mb-6 whitespace-pre-line"
                v-html="description?.body"
              ></div>
            </div>
          </div>
        </div>

        <UButton
          size="xl"
          class="font-semibold rounded-full px-6 self-center block mt-6 mx-auto"
          @click="newQuoteWithDestination(destination.id)"
        >
          {{ $t("pages.destination.createTrip") }}
        </UButton>
      </APageSection>

      <APageSection>
        <AFaqAccordion :questions="faqs" :buttonAttrs="{ class: 'font-extrabold' }" />
      </APageSection>
    </ASiteContainer>
  </div>
</template>

<script lang="ts" setup>
const bannerHeight = ref(550)
const { $device } = useNuxtApp()
const route = useRoute()
const { data: destination, error: destinationError } = await useFetch<
  Bff['/api/destinations/:slug']
>(`/api/destinations/${route.params.slug}`)
assertDefined(destination, destinationError)

const { newQuoteWithDestination } = useQuoteStore()
const trips = ref<any[]>()

useSeoMeta({
  description:
    destination?.value?.metaDescription.length > 80
      ? destination?.value?.metaDescription
      : destination?.value?.title,
  ogDescription:
    destination?.value?.metaDescription.length > 80
      ? destination?.value?.metaDescription
      : destination?.value?.title,
  ogTitle:
    destination?.value?.metaTitle.length > 30
      ? destination?.value?.metaTitle
      : destination?.value?.titleHeader,
  title:
    destination?.value?.metaTitle.length > 30
      ? destination?.value?.metaTitle
      : destination?.value?.titleHeader,
})

function scrollToTrips() {
  const tripsSection = document.querySelector('.trips-section')
  if (tripsSection) {
    tripsSection.scrollIntoView({ behavior: 'smooth' })
  }
}

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    de: '/reiseziele/[slug]',
    en: '/destinations/[slug]',
    es: '/destinos/[slug]',
    fr: '/destinations/[slug]',
    it: '/destinazioni/[slug]',
    nl: '/reisbestemmingen/[slug]',
    pt: '/destinos/[slug]',
  },
})

const localePath = useLocalePath()
const domain = useRequestURL().origin

useHead({
  link: [
    {
      href: `${domain}${localePath({ name: 'destinations-slug', params: { slug: destination?.value?.slug } })}`,
      rel: 'canonical',
    },
  ],
})

const { data: faqs } = await useFetch(`/api/questions/destination`, {
  query: {
    destinationId: destination.value.id,
  },
})

function monthName(monthIndex: number | string) {
  const month = new Date(0, Number(monthIndex)).toLocaleString(navigator.language || 'fr-FR', { month: 'long' })

  return month.length > 3 ? month.slice(0, 3) : month
}

onMounted(() => {
  const banner = document.querySelector('#left-column-banner')
  if (banner) {
    bannerHeight.value = banner.clientHeight - 50
  }
})
</script>
